import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { TextField } from '@mui/material';
import { validInteger } from 'app/helpers/numberHelper';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useSelector } from 'react-redux';
import { selectIsEditMod, selectIsEmiLmoDisabled } from 'store/redux-store/placement-mods/selectors';

export const EmiNumberOfDays = () => {
  const { control, setValue, clearErrors, formState: { errors }, getValues, setError } = useFormContext();
  const { t } = useTranslation();
  const emiNumberOfDays: number | string | null | undefined = useWatch({ name: 'emiNumberOfDays' });
  const moveInDate: Date | string | null | undefined = useWatch({ name: 'moveInDate' });
  const emiDaysWaived: number | string | null | undefined = useWatch({ name: 'emiDaysWaived' });
  const isModEdit = useSelector(selectIsEditMod);
  const { promiseInProgress: isSaveModApiCalling } = usePromiseTracker({ area: 'save-mod-api', delay: 0 });
  const earlyMoveInDate: Date | string | null | undefined = useWatch({ name: 'earlyMoveInDate' });
  const emiCostPerDay: number | string | null | undefined = useWatch({ name: 'emiCostPerDay' });
  const isEmiLmoDisabled = useSelector(selectIsEmiLmoDisabled);
  const [required, setRequired] = useState<boolean>();

  const checkDaysValidation = currentEmiNumberOfDays => {
    if (currentEmiNumberOfDays && !isNullOrUndefined(emiDaysWaived) && !isEmpty(emiDaysWaived)) {
      if (Number(currentEmiNumberOfDays) < Number(emiDaysWaived)) {
        return t('placement.placementMods.daysWaivedError');
      }
    }
    const modifiedDate = isNullOrUndefined(moveInDate)
      ? null
      : moment(moveInDate).subtract(currentEmiNumberOfDays, 'days');
    setValue(
      'earlyMoveInDate',
      isEmpty(currentEmiNumberOfDays) || isNullOrUndefined(currentEmiNumberOfDays)
        ? ''
        : isNullOrUndefined(moveInDate) || isEmpty(moveInDate)
        ? ''
        : modifiedDate.format(),
      { shouldDirty: false },
    );
    clearErrors('earlyMoveInDate');
    if (required && !currentEmiNumberOfDays && currentEmiNumberOfDays !== 0 && modifiedDate) {
      return t('placement.placementMods.numberOfDaysError');
    }

    clearErrors('emiNumberOfDays');
    clearErrors('emiNumberOfDays');
    return null;
  };

  useEffect(() => {
    if (
      (!isNullOrUndefined(earlyMoveInDate) && !isEmpty(earlyMoveInDate)) ||
      (!isNullOrUndefined(getValues('emiCostPerDay')) && getValues('emiCostPerDay') !== '') ||
      (!isNullOrUndefined(getValues('emiNumberOfDays')) && getValues('emiNumberOfDays') !== '') ||
      (!isNullOrUndefined(getValues('emiDaysWaived')) && getValues('emiDaysWaived') !== '')
    ) {
      if (isNullOrUndefined(emiNumberOfDays) || emiNumberOfDays === '') {
        setError('emiNumberOfDays', t('placement.placementMods.numberOfDaysError'));
      } else {
        if (emiNumberOfDays && emiDaysWaived) {
          if (Number(emiDaysWaived) <= Number(emiNumberOfDays)) {
            clearErrors('emiNumberOfDays');
          }
        }
      }
      setRequired(true);
    } else {
      setRequired(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earlyMoveInDate, emiCostPerDay, emiDaysWaived, emiNumberOfDays]);

  useEffect(() => {
    if (!required) {
      setTimeout(() => {
        clearErrors('emiNumberOfDays');
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required]);

  useEffect(() => {
    if (!isNullOrUndefined(earlyMoveInDate) && earlyMoveInDate !== '') {
      const modifiedDate = isNullOrUndefined(moveInDate) ? null : moment(moveInDate).subtract(emiNumberOfDays, 'days');
      setValue(
        'earlyMoveInDate',
        isNullOrUndefined(emiNumberOfDays) || isNullOrUndefined(moveInDate)
          ? ''
          : emiNumberOfDays === 0
          ? moment(moveInDate)
          : modifiedDate.format(),
        { shouldDirty: false },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Controller
      control={control}
      name={'emiNumberOfDays'}
      rules={{
        validate: checkDaysValidation,
        required: required,
      }}
      render={({ field: { onChange, value, ...rest } }) => (
        <TextField
          value={emiNumberOfDays}
          variant="filled"
          fullWidth
          id="emiNumberOfDays"
          color="primary"
          label={t('placement.placementMods.numberofDays')}
          InputProps={{
            readOnly: !isModEdit || isEmiLmoDisabled,
            style: {
              pointerEvents: (isModEdit || isSaveModApiCalling) && !isEmiLmoDisabled ? 'auto' : 'none',
            },
          }}
          InputLabelProps={{
            shrink: emiNumberOfDays ? true : null,
            sx: { marginTop: emiNumberOfDays ? '0px' : '-10px' },
          }}
          onChange={e => {
            if (validInteger(e.target.value, { numberLength: 4 })) {
              setValue('emiNumberOfDays', e.target.value, { shouldDirty: true });
              onChange(e.target.value);
              const modifiedDate = isNullOrUndefined(moveInDate)
                ? null
                : moment(moveInDate).subtract(e.target.value, 'days');
              setValue(
                'earlyMoveInDate',
                e.target.value?.length === 0 ? '' : isNullOrUndefined(moveInDate) ? null : modifiedDate.format(),
                {
                  shouldDirty: true,
                },
              );
            } else {
              setValue('earlyMoveInDate', '', { shouldDirty: true });
            }
            clearErrors('earlyMoveInDate');
          }}
          {...rest}
          disabled={isSaveModApiCalling}
          required={required}
          error={!!errors?.emiNumberOfDays}
          helperText={!isNullOrUndefined(errors?.emiNumberOfDays) ? errors?.emiNumberOfDays?.message : undefined}
        />
      )}
    />
  );
};
